import { useState, useEffect } from 'react'
import SectionHeading from '../HomePage/SectionHeading'
import { PodCastAPI } from '../../apis/PodcastAPI'
import { getErrorMessage, log2 } from '../../helpers/utils'
import { ecomAPI } from '../../apis/Axios'
import ReCAPTCHA from 'react-google-recaptcha'
import { useRef } from 'react'

const CommentSection = ({ post }) => {
    const [comment, setComment] = useState('')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [comments, setComments] = useState([]) // State to store fetched comments
    const [loadingComments, setLoadingComments] = useState(true) // State to handle loading

    const recaptchaRef = useRef()
    const onCaptchaChange = (value) => {
        recaptchaRef.current.value = value
    }

    // Fetch comments when the component mounts
    useEffect(() => {
        const fetchComments = async () => {
            try {
                const fetchedComments = await PodCastAPI.getCommentsByPostId(
                    post.id,
                ) // Assuming this API exists
                log2('Fetched comments:', fetchedComments)
                setComments(fetchedComments)
            } catch (error) {
                console.error('Error fetching comments:', error)
                setError('Failed to load comments. Please try again later.')
            } finally {
                setLoadingComments(false)
            }
        }

        fetchComments()
    }, [post.id])

    const handleSubmit = async () => {
        setIsSubmitting(true)
        setError('')
        setSuccess('')

        try {
            // Basic validation
            if (!comment || !name || !email) {
                throw Error('All fields are required.')
            }

            if (!recaptchaRef.current.value) {
                throw Error('Please complete the reCAPTCHA.')
            }

            const gReCaptchaToken = recaptchaRef.current.value

            // log('captcha', gReCaptchaToken)

            // Post the comment using CommentAPI
            const data = {
                postId: post.id,
                name,
                email,
                comment,
                captcha: gReCaptchaToken,
            }
            await ecomAPI.post('?url=submitComment', data)

            setSuccess('Comment submitted successfully!')
            // Reset form fields
            setComment('')
            setName('')
            setEmail('')

            // Re-fetch comments to include the newly added comment
            const updatedComments = await PodCastAPI.getCommentsByPostId(
                post.id,
            )
            setComments(updatedComments)
        } catch (error) {
            const message =
                getErrorMessage(error) ||
                'Failed to submit the comment. Please try again.'
            setError(message)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="bg-light-yellow-gradient-transparent px-4">
            <div className="container mx-auto py-20">
                <div className="max-w-4xl">
                    <SectionHeading textAlignment="text-left">
                        <p className="olm-gradient-text">Join the Discussion</p>
                    </SectionHeading>

                    <form
                        className="mt-4 space-y-4"
                        onSubmit={(e) => {
                            e.preventDefault()
                            handleSubmit()
                        }}>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Your Comment"
                            className="w-full p-4 border rounded-md"></textarea>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Name"
                            className="w-full p-4 border rounded-md"
                        />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email"
                            className="w-full p-4 border rounded-md"
                        />
                        <div className="mt-1 sm:mt-0 sm:col-span-2">
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={
                                    process.env.NEXT_PUBLIC_RECAPTCHA2_SITE_KEY
                                }
                                onChange={onCaptchaChange}
                                size="normal" // Can be "compact" or "invisible" if needed
                                theme="light" // Can be "dark" if preferred
                            />
                        </div>
                        <button
                            type="submit"
                            className={`px-6 py-3 bg-orange-500 text-white rounded-lg ${
                                isSubmitting
                                    ? 'opacity-50 cursor-not-allowed'
                                    : ''
                            }`}
                            disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Submit Comment'}
                        </button>
                    </form>

                    {error && <p className="text-red-500 mt-4">{error}</p>}
                    {success && (
                        <p className="text-green-500 mt-4">{success}</p>
                    )}

                    <div className="mt-8">
                        <h3 className="text-xl font-bold mb-4">
                            {comments.length} Comments
                        </h3>
                        {loadingComments ? (
                            <p>Loading comments...</p>
                        ) : comments.length > 0 ? (
                            <ul>
                                {comments.map((comment) => (
                                    <li
                                        key={comment.id}
                                        className="border-b py-4 flex items-start space-x-4">
                                        <img
                                            src={
                                                comment.author_avatar_urls['48']
                                            }
                                            alt={`${comment.author_name}'s avatar`}
                                            className="w-12 h-12 rounded-full bg-gray-300"
                                        />
                                        <div>
                                            <p className="text-sm font-semibold">
                                                {comment.author_name}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                {new Date(
                                                    comment.date,
                                                ).toLocaleDateString()}
                                            </p>
                                            <div
                                                className="mt-2 text-gray-800"
                                                dangerouslySetInnerHTML={{
                                                    __html: comment.content
                                                        .rendered,
                                                }}
                                            />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p>No comments yet. Be the first to comment!</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CommentSection
